"use client";

import { FetchButton } from "@/fetch-ui/FetchButton";
import { FetchTooltip } from "@/fetch-ui/FetchTooltip";
import { formatDistanceToNowStrict } from "date-fns";
import { AlertTriangle } from "lucide-react";
import type { ReactNode } from "react";

import { AnimatedAmount } from "../_components/workspace/AnimatedAmount";
import { AssumptionRateEdit } from "../_components/workspace/AssumptionRateEdit";
import { CellSkeleton } from "../_components/workspace/CellSkeleton";
import { DashCell as Dash } from "../_components/workspace/DashCell";
import { useEstimator } from "../_context/EstimatorContext";
import { WHO_TRIGGER_ID } from "../_logic/constants";
import type { AssumptionField, CityEstimate, UsdRange } from "../_logic/types";
import { flightsRange } from "../_logic/estimate";
import { originKey } from "../_logic/workspace";
import { OriginLabel } from "../_components/workspace/OriginLabel";
import { RouteFareCell } from "../_components/workspace/RouteFareCell";
import { HotelSource } from "../_components/workspace/HotelSource";
import { SourcePopover } from "../_components/workspace/SourcePopover";

/**
 * Where a cell is drawn: a single city's full-width row, a city column, a dense column (about
 * 120–170px, captions shortened), or a city card.
 */
export type CellLayout = "wide" | "column" | "narrow" | "card";

/** How a row's values are weighted: the per-person total is the headline. */
export type RowEmphasis = "normal" | "strong" | "muted" | "route";

/** Opens the trip bar's Who editor, where home cities are added. */
const openWho = () => document.getElementById(WHO_TRIGGER_ID)?.click();

/** One row of the comparison, rendered the same way by the table and by the stacked cards. */
export interface ComparisonRow {
  key: string;
  label: string;
  /** Rich label (editable controls) shown instead of `label` where it fits. */
  labelNode?: ReactNode;
  /** Text or an inline editor under the label. */
  hint?: ReactNode;
  emphasis: RowEmphasis;
  /** Whether the cell shows a skeleton, so the table can fade the value in when it lands. */
  loading?: (city: CityEstimate) => boolean;
  cell: (city: CityEstimate, layout: CellLayout) => ReactNode;
}

/**
 * The comparison's rows as a registry, so the wide table and the narrow city cards show the same
 * values, dashes and caveats. Amounts tween when they change; cells waiting on a fare shimmer.
 * @param totalMode - Show every amount for the whole group instead of per person.
 * @param routes - How many home-city fare rows to show (largest first), whether all show, and the toggle.
 * @returns The rows in display order.
 * @throws Never.
 */
export function useComparisonRows(
  totalMode: boolean,
  routes: { limit: number; showAll: boolean; onToggle: () => void }
): ComparisonRow[] {
  // Hooks
  const { workspace, estimate, money, flights, refreshHotels } = useEstimator();

  // Derived
  const { headcount, nights, days, withoutHomeCity } = estimate;
  const hasDates = workspace.dates !== null;
  const hasOrigins = workspace.origins.length > 0;
  const factor = totalMode ? headcount : 1;
  const format = (range: UsdRange) => money.formatRange(range, totalMode);
  const amount = (range: UsdRange): ReactNode => (
    <AnimatedAmount value={{ low: range.low * factor, high: range.high * factor }} format={format} />
  );
  const fromProposals = (node: ReactNode) => (
    <div className="flex flex-col items-end gap-1">
      {node}
      <span className="text-xs font-medium text-indigo-700">From your proposals</span>
    </div>
  );
  const rateLabel = (field: AssumptionField) =>
    workspace.assumptions.edited?.includes(field) ? "Your estimate" : "Default rate, adjusted per city";
  const anyProposals = estimate.cities.some(
    (city) => city.hotel.source === "proposals" || city.sources.meeting === "proposals"
  );
  // Where fares come from, one click from the Flights label.
  const flightsHint = (text: string): ReactNode => (
    <span className="flex flex-wrap items-baseline gap-x-1">
      <span className="whitespace-nowrap">{text}</span>
      <span aria-hidden>·</span>
      <SourcePopover trigger="Live fares" label="Where flight fares come from" title="Flight fares">
        <p className="text-gray-600">
          Live round-trip fares from Google Flights (through SerpAPI) for your dates and cabin, from each home city. The
          average per person is weighted by how many people fly from each city.
          {flights.checkedAt
            ? ` Checked ${formatDistanceToNowStrict(flights.checkedAt, { addSuffix: true })}.`
            : ""}{" "}
          People who live in or near a city drive in and get no flight.
        </p>
      </SourcePopover>
    </span>
  );
  const isLoading = (city: CityEstimate) => city.flights.loading || city.hotel.loading;
  // Short enough for one line in a column, a few words in a narrow one; the tooltip says it in full.
  const unplacedNote = (layout: CellLayout): ReactNode => {
    if (withoutHomeCity === 0) return null;
    const count = withoutHomeCity.toLocaleString();
    return (
      <FetchTooltip
        content={`Leaves out flights for ${count} ${withoutHomeCity === 1 ? "person" : "people"} without a home city. Add their home cities under Who to price them.`}
        side="bottom"
        align="end"
      >
        <span className="cursor-help whitespace-normal text-right text-xs font-medium text-amber-700 text-balance">
          {layout === "narrow" ? `No flights for ${count}` : `No flights for ${count} without a home city`}
        </span>
      </FetchTooltip>
    );
  };
  const groupTotal = (city: CityEstimate, layout: CellLayout): ReactNode => (
    <div className="flex flex-col items-end gap-1">
      <AnimatedAmount value={city.total} format={(range) => money.formatRange(range, true)} />
      {unplacedNote(layout)}
    </div>
  );
  const caveat = (city: CityEstimate): ReactNode => {
    const left = [
      !hasOrigins || city.flights.averageUsd === null ? "flights" : null,
      city.hotel.source === "none" ? "hotel" : null,
    ].filter(Boolean);
    return left.length ? (
      <span className="whitespace-normal text-right text-xs font-medium text-amber-700 text-balance">
        Excludes {left.join(" and ")}
      </span>
    ) : null;
  };

  const perPerson = (city: CityEstimate): ReactNode => (
    <div className="flex flex-col items-end gap-1">
      <AnimatedAmount value={city.perPerson} format={(range) => money.formatRange(range)} />
      {caveat(city)}
    </div>
  );

  const routeRows: ComparisonRow[] = [...workspace.origins]
    .sort((a, b) => b.count - a.count)
    .slice(0, routes.showAll ? Infinity : routes.limit)
    .map((origin) => {
      const pricedAirport =
        estimate.cities
          .flatMap((city) => city.flights.routes)
          .find((route) => route.key === originKey(origin) && route.airports)?.airports?.origin ?? null;
      return {
        key: `route:${originKey(origin)}`,
        label: origin.group ? `${origin.group} from ${origin.city}` : `From ${origin.city}`,
        labelNode: <OriginLabel origin={origin} pricedAirport={pricedAirport} />,
        emphasis: "route",
        cell: (city, layout) => {
          const route = city.flights.routes.find((entry) => entry.key === originKey(origin));
          return route ? (
            <RouteFareCell route={route} destination={city.city} layout={layout} forGroup={totalMode} />
          ) : (
            <Dash />
          );
        },
      };
    });

  // Render
  return [
    ...(workspace.origins.length === 1
      ? []
      : [
          {
            key: "flights",
            label: "Flights",
            hint: !hasOrigins ? (
              <span className="inline-flex flex-wrap items-baseline gap-x-1">
                <FetchButton variant="text" size="xs" color="primary" type="button" onClick={openWho}>
                  Add home cities
                </FetchButton>
                to price flights
              </span>
            ) : (
              flightsHint(totalMode ? "Everyone with a fare" : "Avg per person")
            ),
            emphasis: "normal",
            loading: (city) => hasDates && hasOrigins && city.flights.loading,
            cell: (city) => {
              if (!hasDates || !hasOrigins) return <Dash />;
              // Pending until every route's search has answered: no partial average, no "No fares" yet.
              if (city.flights.loading) return <CellSkeleton />;
              const fares = flightsRange(city.flights, totalMode);
              return (
                <div className="flex flex-col items-end gap-1">
                  {fares === null ? (
                    <span className="text-sm font-medium text-amber-700">No fares</span>
                  ) : (
                    <AnimatedAmount value={fares} format={format} />
                  )}
                  {/* One note for every missing fare; each route's row below says which. */}
                  {city.flights.missing.length > 0 && (
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700">
                      <AlertTriangle aria-hidden className="size-3 shrink-0" />
                      No fare for {city.flights.missing.length} {city.flights.missing.length === 1 ? "route" : "routes"}
                    </span>
                  )}
                </div>
              );
            },
          } satisfies ComparisonRow,
        ]),
    ...routeRows,
    ...(workspace.origins.length > routes.limit
      ? [
          {
            key: "routes-toggle",
            label: "",
            labelNode: (
              <FetchButton variant="text" size="xs" color="neutral" type="button" onClick={routes.onToggle}>
                {routes.showAll ? "Largest home cities only" : `Show all ${workspace.origins.length} home cities`}
              </FetchButton>
            ),
            emphasis: "muted" as const,
            cell: () => null,
          } satisfies ComparisonRow,
        ]
      : []),
    {
      key: "hotel",
      label: hasDates ? `Hotel, ${nights} ${nights === 1 ? "night" : "nights"}` : "Hotel",
      hint: anyProposals ? "Your proposals, else venue rates" : "Per room night × nights",
      emphasis: "normal",
      loading: (city) => hasDates && city.hotel.loading,
      cell: (city, layout) => {
        if (!hasDates) return <Dash />;
        if (city.hotel.loading) return <CellSkeleton captions={1} />;
        if (city.hotel.failed) {
          return (
            <span className="inline-flex items-center gap-1 text-sm font-medium text-gray-500">
              Couldn&apos;t load rates ·
              <FetchButton variant="text" size="xs" color="primary" type="button" onClick={refreshHotels}>
                Retry
              </FetchButton>
            </span>
          );
        }
        if (city.hotel.source === "none") {
          return (
            <span
              title="We have no venue rates for this city, so its hotel is left out of the totals."
              className="cursor-help text-sm font-medium text-gray-500 underline decoration-dotted decoration-gray-300 underline-offset-4"
            >
              No rate data
            </span>
          );
        }
        if (city.hotel.source === "proposals") return fromProposals(amount(city.hotel.total));
        return (
          <div className="flex flex-col items-end gap-1">
            {amount(city.hotel.total)}
            <HotelSource city={city} short={layout === "narrow"} />
          </div>
        );
      },
    },
    {
      key: "meeting",
      label: "Meeting space",
      hint: (
        <span className="inline-flex flex-wrap items-center gap-x-1">
          <span className="text-xs font-medium text-gray-600">{rateLabel("meetingSpacePerDay")} ·</span>
          <AssumptionRateEdit field="meetingSpacePerDay" name="Meeting space" suffix="a day" />
        </span>
      ),
      emphasis: "normal",
      cell: (city) => {
        if (!hasDates) return <Dash />;
        if (city.sources.meeting === "proposals") return fromProposals(amount(city.meetingSpace));
        return amount(city.meetingSpace);
      },
    },
    {
      key: "meals",
      label: "Meals and activities",
      hint: (
        <span className="inline-flex flex-wrap items-center gap-x-1">
          <span className="text-xs font-medium text-gray-600">{rateLabel("mealsPerDay")} ·</span>
          <AssumptionRateEdit
            field="mealsPerDay"
            name="Meals and activities"
            suffix={`a day${hasDates ? `, ${days} ${days === 1 ? "day" : "days"}` : ""}`}
          />
        </span>
      ),
      emphasis: "normal",
      cell: (city) => {
        if (!hasDates) return <Dash />;
        if (city.sources.meals === "proposals") return fromProposals(amount(city.meals));
        return amount(city.meals);
      },
    },
    // The headline and the line under it trade places with the view, so the table keeps its height.
    {
      key: "perPerson",
      label: totalMode ? `Total for ${headcount}` : "Per person",
      emphasis: "strong",
      loading: (city) => hasDates && isLoading(city),
      cell: (city, layout) => {
        if (!hasDates) return <Dash />;
        if (isLoading(city)) return <CellSkeleton width="w-20" />;
        return totalMode ? groupTotal(city, layout) : perPerson(city);
      },
    },
    {
      key: "total",
      label: totalMode ? "Per person" : `Total for ${headcount || "the group"}`,
      emphasis: "muted",
      loading: (city) => hasDates && headcount > 0 && isLoading(city),
      cell: (city, layout) => {
        if (!hasDates || headcount === 0) return <Dash />;
        if (isLoading(city)) return <CellSkeleton />;
        return totalMode ? perPerson(city) : groupTotal(city, layout);
      },
    },
  ];
}
